/**
 * Controls automatic play/advance of the carousel.
 * Uses CSS @keyframes + @property for smooth progress animation
 * and setTimeout for slide-advance timing — no rAF loop needed.
 */

let cssInjected = false;

function injectCSS() {
	if (cssInjected) return;
	cssInjected = true;
	const style = document.createElement('style');
	style.textContent = [
		'@property --tarot-autoplay-progress {',
		'  syntax: "<number>";',
		'  inherits: true;',
		'  initial-value: 0;',
		'}',
		'@keyframes tarot-autoplay-progress {',
		'  from { --tarot-autoplay-progress: 0; }',
		'  to   { --tarot-autoplay-progress: 1; }',
		'}',
	].join('\n');
	document.head.appendChild(style);
}

export default class Autoplay {
	static pluginName = 'autoplay';

	/**
	 * @constructor
	 * @param {Object} ctx - the carousel context to control autoplay for
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.autoplayOptions = _.ctx.store.getOptions().autoplay || {};

		_.isRunning = false;
		_.isSuspended = false;
		_.interval = 0;
		_.timerId = null;
		_.remainingTime = 0;
		_.runToken = 0;

		// create debounced resume function
		_.debouncedResume = _.ctx.utils.debounce(() => _.start(), 10000);

		// define all event handlers in one object
		_.handlers = {
			userInteracted: () => {
				_.pause();
			},

			optionsChanged: () => {
				_.autoplayOptions = _.ctx.store.getOptions().autoplay || {};
				_.reInit();
			},

			canLoopChanged: ({ canLoop }) => {
				const options = _.ctx.store.getOptions();
				if (options.loop && !canLoop) {
					_.stop();
				}
			},

			windowFocused: () => {
				if (_.isSuspended) _.resume();
			},

			windowBlurred: () => {
				if (_.isRunning && !_.isSuspended) _.suspend();
			},

			visibilityChanged: ({ hidden }) => {
				if (hidden) {
					if (_.isRunning && !_.isSuspended) _.suspend();
				} else {
					if (_.isSuspended) _.resume();
				}
			},
		};

		injectCSS();
		_.init();
	}

	/**
	 * initialize autoplay
	 */
	init() {
		this.bindEvents();
		this.reInit();
	}

	/**
	 * reinitialize on carousel option changes
	 */
	reInit() {
		const _ = this;

		// reload options
		_.autoplayOptions = _.ctx.store.getOptions().autoplay || {};

		// check options
		if (_.autoplayOptions.interval) {
			_.start();
		} else {
			_.stop();
		}
	}

	/**
	 * bind carousel events to handlers
	 */
	bindEvents() {
		const _ = this;
		_.ctx.emitter.on(_.ctx.events.user.interacted, _.handlers.userInteracted);
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.on(_.ctx.events.store.canLoopChanged, _.handlers.canLoopChanged);
		_.ctx.emitter.on(_.ctx.events.window.hasFocus, _.handlers.windowFocused);
		_.ctx.emitter.on(_.ctx.events.window.lostFocus, _.handlers.windowBlurred);
		_.ctx.emitter.on(_.ctx.events.window.visibilityChange, _.handlers.visibilityChanged);
	}

	/** toggle aria-live on the announcements element based on autoplay state */
	updateAriaLive() {
		const announcements = this.ctx.announcements;
		if (!announcements) return;
		announcements.setAttribute('aria-live', this.isRunning ? 'off' : 'polite');
	}

	/**
	 * start the CSS keyframe animation on the carousel element
	 */
	startAnimation() {
		const el = this.ctx.carousel;
		el.style.animation = 'none';
		void el.offsetHeight; // force reflow to restart
		el.style.animation = `tarot-autoplay-progress ${this.interval}ms linear forwards`;
		el.style.animationPlayState = 'running';
	}

	/**
	 * stop the CSS animation — @property initial-value resets progress to 0
	 */
	stopAnimation() {
		const el = this.ctx.carousel;
		el.style.animation = 'none';
	}

	/**
	 * start the autoplay timer
	 */
	start() {
		const _ = this;
		_.stop();

		if (!_.autoplayOptions.interval) return;

		let interval = _.autoplayOptions.interval;
		if (interval === 0 || interval === false) return;
		if (interval === true) interval = 4000;
		if (!Number.isFinite(interval) || interval <= 0) return;

		// don't autoplay if loop is enabled but can't actually loop
		const options = _.ctx.store.getOptions();
		const state = _.ctx.store.getState();
		if (options.loop && !state.canLoop) return;

		// don't animate if already on last slide with no loop/rewind
		if (!options.loop && !_.autoplayOptions.rewind && state.pageIndex >= state.pageCount - 1) return;

		_.interval = interval;
		_.isRunning = true;
		_.isSuspended = false;
		_.remainingTime = interval;
		_.runToken++;
		_.updateAriaLive();

		const token = _.runToken;

		_.startAnimation();
		_.timerId = setTimeout(() => {
			if (_.runToken === token) _.advance();
		}, interval);
	}

	/**
	 * advance to next slide and restart countdown
	 */
	advance() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		const state = _.ctx.store.getState();

		// stop at end when loop is disabled (or rewind)
		if (!options.loop && state.pageIndex >= state.pageCount - 1) {
			if (_.autoplayOptions.rewind) {
				_.ctx.commands.goToSlide(0, -5);
			} else {
				_.stop();
				return;
			}
		} else {
			_.ctx.commands.next(-5);
		}

		// if we just landed on the last slide with no loop/rewind, stop
		const newState = _.ctx.store.getState();
		if (!options.loop && !_.autoplayOptions.rewind && newState.pageIndex >= newState.pageCount - 1) {
			_.stop();
			return;
		}

		// restart animation + timer
		_.remainingTime = _.interval;
		_.runToken++;
		const token = _.runToken;

		_.startAnimation();
		_.timerId = setTimeout(() => {
			if (_.runToken === token) _.advance();
		}, _.interval);
	}

	/**
	 * suspend autoplay (window blur / visibility hidden) — preserves position
	 */
	suspend() {
		const _ = this;
		if (!_.isRunning || _.isSuspended) return;

		_.isSuspended = true;
		_.ctx.carousel.style.animationPlayState = 'paused';

		if (_.timerId !== null) {
			clearTimeout(_.timerId);
			_.timerId = null;
		}

		// compute remaining time from animation progress
		const progress = getComputedStyle(_.ctx.carousel)
			.getPropertyValue('--tarot-autoplay-progress');
		const elapsed = parseFloat(progress) * _.interval;
		_.remainingTime = Math.max(0, Math.min(_.interval, _.interval - elapsed));
	}

	/**
	 * resume after suspend — picks up from where it left off
	 */
	resume() {
		const _ = this;
		if (!_.isSuspended) return;

		_.isSuspended = false;
		_.ctx.carousel.style.animationPlayState = 'running';

		_.runToken++;
		const token = _.runToken;

		_.timerId = setTimeout(() => {
			if (_.runToken === token) _.advance();
		}, _.remainingTime);
	}

	/**
	 * pause autoplay temporarily (user interaction) — resets progress to 0
	 */
	pause() {
		const _ = this;
		_.isRunning = false;
		_.isSuspended = false;
		_.stopAnimation();

		if (_.timerId !== null) {
			clearTimeout(_.timerId);
			_.timerId = null;
		}
		_.runToken++;
		_.updateAriaLive();

		// restarts in 10 seconds if no interaction
		if (_.autoplayOptions.interval) {
			_.debouncedResume();
		}
	}

	/**
	 * stop the autoplay timer completely
	 */
	stop() {
		const _ = this;
		_.isRunning = false;
		_.isSuspended = false;
		_.runToken++;

		if (_.timerId !== null) {
			clearTimeout(_.timerId);
			_.timerId = null;
		}

		if (_.ctx?.carousel) {
			_.stopAnimation();
		}
		_.updateAriaLive();
	}

	/**
	 * destroy the autoplay, unbinding all events and clearing timers
	 */
	destroy() {
		const _ = this;

		_.stop();

		// remove inline animation styles
		if (_.ctx?.carousel) {
			_.ctx.carousel.style.removeProperty('animation');
			_.ctx.carousel.style.removeProperty('animation-play-state');
		}

		// unbind from carousel events
		_.ctx.emitter.off(_.ctx.events.user.interacted, _.handlers.userInteracted);
		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.off(_.ctx.events.store.canLoopChanged, _.handlers.canLoopChanged);
		_.ctx.emitter.off(_.ctx.events.window.hasFocus, _.handlers.windowFocused);
		_.ctx.emitter.off(_.ctx.events.window.lostFocus, _.handlers.windowBlurred);
		_.ctx.emitter.off(
			_.ctx.events.window.visibilityChange,
			_.handlers.visibilityChanged,
		);

		// clear the debounced function
		if (_.debouncedResume.cancel) {
			_.debouncedResume.cancel();
		}
	}
}
